import { Sprout, Download, Share2, QrCode, ShieldCheck, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { memberProfile, koperasiInfo, formatDate } from "@/lib/dummyData";
import { useAuth } from "@/lib/auth";
import { useEffect, useState } from "react";
import QRCode from "qrcode";

export default function MemberCard() {
  const { user } = useAuth();
  const [qr, setQr] = useState(null);

  // Data anggota nyata (fallback ke contoh bila belum termuat).
  const m = {
    name: user?.name || memberProfile.name,
    id: user?.koperasi_member_number || memberProfile.id,
    email: user?.email || memberProfile.email,
    phone: user?.phone_masked || memberProfile.phone,
    referral: user?.referral_code || "-",
    joinedAt: user?.created_at || memberProfile.joinedAt,
    status: "Anggota Aktif",
  };
  const initials = m.name.split(" ").map((p) => p[0]).slice(0, 2).join("");

  // Render QR unik anggota dari nomor keanggotaan.
  useEffect(() => {
    QRCode.toDataURL(`KOPMEMBER:${m.id}`, { width: 160, margin: 1 }).then(setQr).catch(() => {});
  }, [m.id]);

  return (
    <div className="space-y-7">
      <div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Kartu Anggota Digital</h1>
        <p className="text-ink/75 mt-2">Identitas resmi sebagai anggota. Tunjukkan QR saat transaksi di cabang.</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6 items-start">
        {/* Card visual */}
        <div className="lg:col-span-7">
          <div className="relative aspect-[1.586/1] max-w-xl rounded-3xl overflow-hidden shadow-2xl mx-auto" data-testid="member-card-visual">
            <div className="absolute inset-0 bg-forest grain-overlay"></div>
            <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-amber-warm/30 blur-3xl"></div>
            <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full bg-sage/30 blur-2xl"></div>

            <div className="relative z-10 p-7 lg:p-9 h-full flex flex-col justify-between text-cream">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="w-9 h-9 rounded-xl bg-amber-warm text-ink grid place-items-center"><Sprout size={16} /></span>
                    <div className="leading-tight">
                      <div className="font-display font-bold">{koperasiInfo.name}</div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-amber-warm">Kartu Anggota</div>
                    </div>
                  </div>
                </div>
                <Award className="text-amber-warm" size={22} />
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-amber-warm font-semibold mb-1">Anggota</div>
                <div className="font-display font-bold text-2xl lg:text-3xl">{m.name}</div>
                <div className="font-mono text-amber-warm text-sm tracking-wider mt-1">{m.id}</div>
              </div>

              <div className="flex items-end justify-between">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.22em] text-cream/60">Bergabung sejak</div>
                  <div className="font-semibold text-sm">{formatDate(m.joinedAt)}</div>
                </div>
                <div className="w-20 h-20 rounded-xl bg-white p-2 grid place-items-center">
                  {qr ? <img src={qr} alt="QR anggota" className="w-full h-full" /> : <QrCode size={64} className="text-ink" />}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-6 justify-center">
            <Button onClick={() => toast.success("Kartu diunduh sebagai PDF")} className="rounded-full bg-forest hover:bg-forest-dark text-white" data-testid="card-download">
              <Download size={15} /> Unduh PDF
            </Button>
            <Button onClick={() => toast.info("Tautan kartu disalin")} variant="outline" className="rounded-full border-stone-soft" data-testid="card-share">
              <Share2 size={15} /> Bagikan
            </Button>
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-5 space-y-4">
          <Card className="rounded-2xl border-stone-soft p-6">
            <div className="flex items-center gap-3">
              <Avatar className="w-14 h-14 ring-2 ring-amber-warm/40">
                <AvatarFallback className="bg-forest/10 text-forest font-bold">{initials}</AvatarFallback>
              </Avatar>
              <div>
                <div className="font-display font-bold text-ink">{m.name}</div>
                <div className="text-xs text-ink/55">{m.email}</div>
              </div>
            </div>
            <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <dt className="text-ink/55">Status</dt><dd className="font-semibold text-forest">{m.status}</dd>
              <dt className="text-ink/55">No. Anggota</dt><dd className="font-semibold text-ink font-mono text-xs">{m.id}</dd>
              <dt className="text-ink/55">Kode Referral</dt><dd className="font-semibold text-amber-700 font-mono">{m.referral}</dd>
              <dt className="text-ink/55">Telepon</dt><dd className="font-semibold text-ink">{m.phone}</dd>
            </dl>
          </Card>

          <Card className="rounded-2xl border-stone-soft p-6 bg-cream">
            <div className="flex gap-3">
              <ShieldCheck className="text-forest shrink-0" size={22} />
              <div>
                <div className="font-display font-semibold text-ink">Keamanan Kartu Digital</div>
                <p className="text-xs text-ink/75 mt-1 leading-relaxed">
                  QR Code di kartu ini hanya valid 24 jam dan unik per anggota. Jangan bagikan tangkapan layar ke pihak lain selain petugas resmi koperasi.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
